import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Mail, Lock, Loader2, LogIn, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyOtp, resendOtp, forgotPassword, isAuthenticated, isAdmin, user, loading: authLoading } = useAuth();

  const [mode, setMode] = useState("credentials"); // credentials | otp | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isAdmin ? "/" : "/projects"} replace />;
  }

  const routeAfterLogin = (u) => navigate(u.role === "admin" ? "/" : "/projects");

  const handleCredentials = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter your email and password"); return; }
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      setMode("otp");
      toast.success("We've emailed you a 6-digit code");
    } catch (error) {
      toast.error("Login failed", { description: error.response?.data?.detail || "Invalid email or password" });
    } finally { setLoading(false); }
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const u = await verifyOtp(email.trim().toLowerCase(), code.trim());
      toast.success(`Welcome, ${u.name}!`);
      routeAfterLogin(u);
    } catch (error) {
      toast.error("Verification failed", { description: error.response?.data?.detail || "Incorrect code" });
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    try { await resendOtp(email.trim().toLowerCase()); toast.success("A new code is on its way"); }
    catch (error) { toast.error(error.response?.data?.detail || "Please wait a moment"); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      toast.success("If that email exists, a reset link has been sent");
      setMode("credentials");
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-30"></div>
      </div>

      <div className="relative w-full max-w-md px-6">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Layers className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">
              Support<span className="text-blue-600">IQ</span>
            </h1>
            <p className="text-gray-500 mt-2">
              {mode === "credentials" && "Sign in to your account"}
              {mode === "otp" && "Enter your verification code"}
              {mode === "forgot" && "Reset your password"}
            </p>
          </div>

          {mode === "credentials" && (
            <form onSubmit={handleCredentials} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@nassauengineering.ie" className="pl-10 h-11" data-testid="login-email" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" className="pl-10 h-11" data-testid="login-password" />
                </div>
              </div>
              <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={loading} data-testid="login-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5 mr-2" />Sign In</>}
              </Button>
              <button type="button" onClick={() => setMode("forgot")}
                className="w-full text-sm text-blue-600 hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </button>
            </form>
          )}

          {mode === "otp" && (
            <form onSubmit={handleOtp} className="space-y-5">
              <p className="text-sm text-gray-600 text-center">
                We sent a 6-digit code to <span className="font-medium">{email}</span>
              </p>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input type="text" inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000" className="pl-10 h-11 tracking-[0.4em] text-center text-lg"
                  data-testid="login-otp" />
              </div>
              <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={loading} data-testid="verify-otp-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify & Sign In"}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => { setMode("credentials"); setCode(""); }}
                  className="text-gray-500 hover:underline flex items-center gap-1" data-testid="otp-back">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="button" onClick={handleResend} className="text-blue-600 hover:underline" data-testid="resend-otp">
                  Resend code
                </button>
              </div>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={handleForgot} className="space-y-5">
              <p className="text-sm text-gray-600">Enter your email and we'll send you a link to reset your password.</p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@nassauengineering.ie" className="pl-10 h-11" data-testid="forgot-email" />
              </div>
              <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={loading} data-testid="forgot-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset link"}
              </Button>
              <button type="button" onClick={() => setMode("credentials")}
                className="w-full text-sm text-gray-500 hover:underline flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          © {new Date().getFullYear()} Support IQ · Nassau Engineering
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
